/**
 * Notification sound service. Tones are synthesized on the fly with the
 * Web Audio API — no .mp3 assets, zero bundle weight, customizable per
 * event type. Ported from the legacy v1 service.
 *
 * Browsers gate AudioContext until a user gesture, so the first call to
 * `play()` may be silent. The authenticated app initializer calls `unlock()` on the
 * first click/keydown so subsequent plays work even when the tab is
 * backgrounded.
 */

export type NotificationSoundType =
  | 'customer_message'
  | 'agent_message'
  | 'mention'
  | 'new_conversation'
  | 'transfer'
  | 'system';

interface SoundConfig {
  frequencies: number[];
  durations: number[];
  volume: number;
  type: OscillatorType;
}

const SOUND_CONFIGS: Record<NotificationSoundType, SoundConfig> = {
  customer_message: {
    frequencies: [523.25, 659.25],
    durations: [0.12, 0.15],
    volume: 0.3,
    type: 'sine',
  },
  agent_message: {
    frequencies: [440],
    durations: [0.1],
    volume: 0.25,
    type: 'triangle',
  },
  mention: {
    frequencies: [880, 988, 1047],
    durations: [0.08, 0.08, 0.12],
    volume: 0.35,
    type: 'sine',
  },
  new_conversation: {
    frequencies: [784, 659],
    durations: [0.15, 0.2],
    volume: 0.35,
    type: 'sine',
  },
  transfer: {
    frequencies: [523.25, 783.99, 1046.5],
    durations: [0.1, 0.1, 0.18],
    volume: 0.4,
    type: 'sine',
  },
  system: {
    frequencies: [600],
    durations: [0.1],
    volume: 0.2,
    type: 'square',
  },
};

const STORAGE_KEYS = {
  enabled: 'blaze.notif.sound.enabled',
  volume: 'blaze.notif.sound.volume',
};

class NotificationSoundService {
  private audioContext: AudioContext | null = null;
  private enabled = true;
  private volume = 0.5;
  private unlocked = false;

  constructor() {
    if (typeof window === 'undefined') return;
    const en = localStorage.getItem(STORAGE_KEYS.enabled);
    if (en !== null) this.enabled = en !== 'false';
    const vol = localStorage.getItem(STORAGE_KEYS.volume);
    if (vol !== null) {
      const n = parseFloat(vol);
      if (Number.isFinite(n)) this.volume = Math.max(0, Math.min(1, n));
    }
  }

  /** Must be called from a user gesture (click/keydown) so the audio
   *  context is allowed to play in the background later. */
  unlock(): void {
    if (this.unlocked) return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.001);
    this.unlocked = true;
  }

  private ensureContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.audioContext) {
      try {
        const Ctx =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!Ctx) return null;
        this.audioContext = new Ctx();
      } catch {
        return null;
      }
    }
    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume();
    }
    return this.audioContext;
  }

  play(type: NotificationSoundType): void {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    const cfg = SOUND_CONFIGS[type];
    if (!cfg) return;

    const start = ctx.currentTime;
    let offset = 0;
    for (let i = 0; i < cfg.frequencies.length; i++) {
      const freq = cfg.frequencies[i];
      const dur = cfg.durations[i];
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = cfg.type;
      osc.frequency.setValueAtTime(freq, start + offset);

      const peak = cfg.volume * this.volume;
      gain.gain.setValueAtTime(0, start + offset);
      gain.gain.linearRampToValueAtTime(peak, start + offset + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.01, start + offset + dur);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start + offset);
      osc.stop(start + offset + dur + 0.01);
      offset += dur;
    }
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    try {
      localStorage.setItem(STORAGE_KEYS.enabled, String(v));
    } catch {
      // ignore quota errors
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    try {
      localStorage.setItem(STORAGE_KEYS.volume, String(this.volume));
    } catch {
      // ignore
    }
  }

  getVolume(): number {
    return this.volume;
  }
}

export const notificationSound = new NotificationSoundService();
